/**
 * MarkdownEditor — a WYSIWYG editor whose value is Markdown.
 *
 * Why this exists: several screens (meeting minutes, etc.) store their body as
 * Markdown so the server can compose/consume it and `react-markdown` can render
 * it read-only. Editing that Markdown in a raw <textarea> means users stare at
 * `#`, `|`, `**` syntax. This component wraps Tiptap so the user edits the
 * *formatted* document (bold, headings, GFM tables, lists, quotes) and never
 * sees Markdown markup — yet the value in/out is still a Markdown string, so
 * the existing save path, preview, and read-only render are untouched.
 *
 * Contract:
 *   value     Markdown string (source of truth lives in the parent).
 *   onChange  called with the new Markdown string on every edit.
 *
 * Round-trip: `tiptap-markdown` parses `value` -> ProseMirror doc on load and
 * serializes the doc -> Markdown on change. We only push `value` back into the
 * editor when it differs from what the editor itself last emitted, so external
 * updates (e.g. "Generate from agenda" replacing the draft) flow in without
 * clobbering the caret mid-typing.
 */
import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Table as TableIcon,
  Undo2,
  Redo2,
} from "lucide-react";

/** tiptap-markdown augments editor.storage.markdown but ships no core-Storage
 * type augmentation for Tiptap v3, so we read it through a narrow cast. */
function getMarkdown(editor: Editor): string {
  return (
    editor.storage as { markdown?: { getMarkdown(): string } }
  ).markdown?.getMarkdown() ?? "";
}

interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  /** Passed through to the editable region for testing. */
  "data-testid"?: string;
  /** Disable all editing (finalised minutes, no rights, etc.). */
  editable?: boolean;
}

/** One toolbar button — active state mirrors the current selection. */
function ToolBtn({
  onClick,
  active,
  disabled,
  title,
  children,
  testid,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <button
      type="button"
      className={
        "md-editor-toolbar__btn" + (active ? " md-editor-toolbar__btn--active" : "")
      }
      // Keep focus in the editor so the command applies to the live selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  // Re-render the toolbar on every selection/transaction so active states and
  // undo/redo enablement stay in sync. useEditorState would be lighter, but a
  // forced tick keeps the dependency surface minimal.
  const [, force] = React.useReducer((x) => x + 1, 0);
  useEffect(() => {
    editor.on("transaction", force);
    editor.on("selectionUpdate", force);
    return () => {
      editor.off("transaction", force);
      editor.off("selectionUpdate", force);
    };
  }, [editor]);

  const c = editor.chain().focus();
  return (
    <div className="md-editor-toolbar" data-testid="TB_markdown_editor">
      <ToolBtn
        title="Bold"
        active={editor.isActive("bold")}
        onClick={() => c.toggleBold().run()}
        testid="BTN_md_bold"
      >
        <BoldIcon size={15} />
      </ToolBtn>
      <ToolBtn
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => c.toggleItalic().run()}
        testid="BTN_md_italic"
      >
        <ItalicIcon size={15} />
      </ToolBtn>
      <span className="md-editor-toolbar__sep" />
      <ToolBtn
        title="Heading 1"
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => c.toggleHeading({ level: 1 }).run()}
        testid="BTN_md_h1"
      >
        <Heading1 size={15} />
      </ToolBtn>
      <ToolBtn
        title="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => c.toggleHeading({ level: 2 }).run()}
        testid="BTN_md_h2"
      >
        <Heading2 size={15} />
      </ToolBtn>
      <ToolBtn
        title="Heading 3"
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => c.toggleHeading({ level: 3 }).run()}
        testid="BTN_md_h3"
      >
        <Heading3 size={15} />
      </ToolBtn>
      <span className="md-editor-toolbar__sep" />
      <ToolBtn
        title="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => c.toggleBulletList().run()}
        testid="BTN_md_ul"
      >
        <List size={15} />
      </ToolBtn>
      <ToolBtn
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => c.toggleOrderedList().run()}
        testid="BTN_md_ol"
      >
        <ListOrdered size={15} />
      </ToolBtn>
      <ToolBtn
        title="Quote"
        active={editor.isActive("blockquote")}
        onClick={() => c.toggleBlockquote().run()}
        testid="BTN_md_quote"
      >
        <Quote size={15} />
      </ToolBtn>
      <span className="md-editor-toolbar__sep" />
      <ToolBtn
        title="Insert table"
        onClick={() =>
          c.insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run()
        }
        testid="BTN_md_table"
      >
        <TableIcon size={15} />
      </ToolBtn>
      <span className="md-editor-toolbar__sep" />
      <ToolBtn
        title="Undo"
        disabled={!editor.can().undo()}
        onClick={() => c.undo().run()}
        testid="BTN_md_undo"
      >
        <Undo2 size={15} />
      </ToolBtn>
      <ToolBtn
        title="Redo"
        disabled={!editor.can().redo()}
        onClick={() => c.redo().run()}
        testid="BTN_md_redo"
      >
        <Redo2 size={15} />
      </ToolBtn>
    </div>
  );
}

export default function MarkdownEditor({
  value,
  onChange,
  placeholder,
  editable = true,
  "data-testid": dataTestid,
}: MarkdownEditorProps) {
  // The last Markdown we emitted — lets us distinguish "parent echoed our own
  // change back" (ignore) from "parent set a genuinely new value" (reload).
  const lastEmitted = useRef<string>(value);

  const editor = useEditor({
    editable,
    // Avoid the v3 hydration flush warning; content renders on the next tick.
    immediatelyRender: false,
    extensions: [
      StarterKit,
      // GFM tables — the minutes are table-heavy.
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: placeholder || "" }),
      // Markdown in/out. transformPastedText lets users paste Markdown and have
      // it become formatted content rather than literal `#`/`|`.
      Markdown.configure({
        html: false,
        tightLists: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const md = getMarkdown(editor);
      lastEmitted.current = md;
      onChange(md);
    },
  });

  // Push external value changes (e.g. Generate-from-agenda) into the editor
  // without disturbing the caret when the change originated here.
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmitted.current) return;
    const current = getMarkdown(editor);
    if (value === current) return;
    lastEmitted.current = value;
    // emitUpdate:false = don't fire onUpdate for this programmatic set (v3 API).
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  return (
    <div className="md-editor">
      {editable && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="md-editor__content meeting-minutes-render"
        data-testid={dataTestid}
      />
    </div>
  );
}
